import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import React from "react";
import { IntegrationAccountDto } from "../../../api/integrationAccountsApi";

interface DeleteIntegrationAccountDialogProps {
  open: boolean;
  account: IntegrationAccountDto | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const DeleteIntegrationAccountDialog: React.FC<
  DeleteIntegrationAccountDialogProps
> = ({ open, account, onClose, onConfirm, isLoading }) => {
  if (!account) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        Confirm Delete
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to delete this integration account? This action
          cannot be undone.
        </DialogContentText>

        {/* Account Info */}
        <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Account Information
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {account.accountName}
            </Typography>
            <Chip
              label={account.integrationTypeName}
              color="primary"
              size="small"
              variant="outlined"
            />
          </Box>

          {account.apiKey && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
              fontFamily="monospace"
            >
              API Key: {account.apiKey}
            </Typography>
          )}

          {account.lastSyncedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Last Synced:{" "}
              {new Date(account.lastSyncedAt).toLocaleString()}
            </Typography>
          )}
        </Box>

        <DialogContentText color="error.main" sx={{ fontWeight: 500 }}>
          ⚠️ This will permanently delete the integration account and may affect
          data synchronization. Any vehicles or trailers linked to this account
          may lose their integration data.
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          size="medium"
          sx={{
            fontSize: "0.8125rem",
            textTransform: "none",
            height: "36px",
            minWidth: "64px",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={isLoading}
          size="medium"
          sx={{
            fontSize: "0.8125rem",
            textTransform: "none",
            fontWeight: 500,
            height: "36px",
            minWidth: "64px",
          }}
        >
          {isLoading ? "Deleting..." : "Delete Account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteIntegrationAccountDialog;

